import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import api from '../services/api';
import axios from 'axios';
import Navbar from '../components/Navbar';
import TagSelector from '../components/TagSelector';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Prompt shown in the manual-extraction modal (mirrors backend _EXTRACTION_PROMPT)
const MANUAL_EXTRACTION_PROMPT = `Extract ALL medical data from this report and return ONLY valid JSON in this exact format.

IMPORTANT INSTRUCTIONS:
- Extract ALL test parameters, not just abnormal ones
- Include every single test result from every section (CBC, LFT, KFT, Lipids, Thyroid, Vitamins, etc.)
- Only include lab results that have an actual numeric or text result value — SKIP tests that are mentioned or ordered but have no recorded value
- Do NOT skip normal results - include everything
- For each test include: exact test name as shown in report, numeric value only, unit, reference range
- Set is_abnormal to true if value is outside reference range

=== RULES FOR LAB RESULTS ===
- NEVER append dates to test_name. Dates belong in report_date only.
  BAD: "BSL Fasting (30/6/25)"  GOOD: "BSL Fasting" with report_date "2025-06-30"
- EXCLUDE tests where value has no medical meaning: Skip if value is "Sent", "Done", "Pending", "N/A", "--", "-", "WNL", blank, or purely administrative (lab charges in Rs, counts of tablets)
- EXCLUDE rows that are clearly administrative: lab charges, tablet counts, billing entries
  Examples to skip: "BSLPP x8 = Rs 640", "14 tabs", "1 bottle"
- For tests with multiple readings on different dates (like serial blood sugars): create separate entries with different report_date values, NOT date in test_name
  BAD:  [{"test_name": "BSL Fasting (27/6/25)"}, {"test_name": "BSL Fasting (28/6/25)"}]
  GOOD: [{"test_name": "BSL Fasting", "report_date": "2025-06-27"}, {"test_name": "BSL Fasting", "report_date": "2025-06-28"}]
- For qualitative/status tests (Histopathology, Blood Group, BRCA): only include if value is a meaningful result (e.g. "Positive"/"Negative"/"O Positive"). Skip if value is just a status like "Sent", "Done", "Awaited"

=== RULES FOR VITALS ===
- Pulse, Heart Rate, PR → vital_type: "heart_rate"
- SpO2, Oxygen Saturation → vital_type: "spo2"
- Temperature → vital_type: "temperature", include unit (F or C)
- Blood Pressure → vital_type: "blood_pressure", systolic and diastolic as separate numeric fields
- Weight → vital_type: "weight"
- For ophthalmology documents: IOP (intraocular pressure / tonometry) → vital_type: "intraocular_pressure" with right_eye and left_eye as separate numeric values

=== RULES FOR BLOOD SUGAR MONITORING LOGS ===
If the report has multiple blood sugar readings over multiple days (common in hospital stays, surgery reports), use blood_sugar_monitoring_log — do NOT add these as individual lab_results entries with dates in test_name.
timing values: "Fasting" | "Post Lunch" | "Post Prandial" | "Pre-Op" | "Post-Op" | "Random"

=== RULES FOR RADIOLOGY ===
Include type, body_part, date, impression, and ALL numeric measurements/findings in observations.
Common types: USG, CT, MRI, X-Ray, CXR, ECG, 2D Echo, OCT, Biometry, PET-CT, Mammography

Return ONLY this JSON, no explanation:

{
  "patient_name": "Full Name",
  "report_date": "YYYY-MM-DD",
  "doctor_name": "Dr. Name",
  "hospital_name": "Hospital/Lab Name",
  "lab_results": [
    {"test_name": "Test Name", "value": "7.0", "unit": "mg/dL", "reference_range": "4.0-6.0", "category": "General", "is_abnormal": false}
  ],
  "vitals": [
    {"vital_type": "blood_pressure", "systolic": 120, "diastolic": 80, "recorded_at": "YYYY-MM-DD"},
    {"vital_type": "intraocular_pressure", "right_eye": 17, "left_eye": 16, "unit": "mmHg"}
  ],
  "blood_sugar_monitoring_log": [
    {"date": "2025-06-27", "timing": "Fasting", "value": "95", "unit": "mg/dL", "is_abnormal": false}
  ],
  "radiology": [
    {"type": "CT", "body_part": "Chest", "date": "2025-06-28", "impression": "No abnormality detected", "observations": "ALL numeric measurements and detailed findings here"}
  ],
  "prescriptions": [
    {"name": "Medicine Name", "dosage": "10mg", "frequency": "Once daily", "duration": "30 days"}
  ]
}`;

const DOCUMENT_TYPES = [
  { value: 'prescription',      label: 'Prescription' },
  { value: 'lab_report',        label: 'Lab Report' },
  { value: 'discharge_summary', label: 'Discharge Summary' },
  { value: 'xray_scan',         label: 'X-Ray / Scan' },
  { value: 'insurance',         label: 'Insurance' },
  { value: 'vaccination',       label: 'Vaccination' },
  { value: 'other',             label: 'Other' },
];

// ── Shimmer skeleton for JSON box ────────────────────────────────────────────
const shimmerStyle = {
  background: 'linear-gradient(90deg, #f0f4f8 25%, #e2e8f0 50%, #f0f4f8 75%)',
  backgroundSize: '200% 100%',
  animation: 'shimmer 1.4s infinite',
};

const ShimmerLines = () => (
  <div style={{ padding: '14px 12px' }}>
    <style>{`@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }`}</style>
    {[80, 60, 90, 50, 70, 40, 85].map((w, i) => (
      <div key={i} style={{ ...shimmerStyle, height: '13px', borderRadius: '6px', marginBottom: '10px', width: `${w}%` }} />
    ))}
    <div style={{ color: '#a0aec0', fontSize: '0.8rem', textAlign: 'center', marginTop: '8px' }}>
      AI is reading your document…
    </div>
  </div>
);

// ── LLM badge ────────────────────────────────────────────────────────────────
function LLMBanner({ llmConfig, onChangeClick }) {
  if (!llmConfig) return null;
  const label = `${llmConfig.provider}${llmConfig.model ? ` — ${llmConfig.model}` : ''}`;
  return (
    <div style={{
      background: '#ebf4ff', border: '1px solid #bee3f8', borderRadius: '10px',
      padding: '12px 16px', marginBottom: '24px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <span style={{ fontSize: '1.3rem' }}>🤖</span>
        <div>
          <div style={{ fontWeight: '600', color: '#2b6cb0', fontSize: '0.9rem' }}>
            AI extraction ready
          </div>
          <div style={{ color: '#4a5568', fontSize: '0.82rem' }}>{label}</div>
        </div>
      </div>
      <button
        type="button"
        onClick={onChangeClick}
        style={{
          background: 'none', border: '1px solid #90cdf4', color: '#2b6cb0',
          borderRadius: '6px', padding: '5px 12px', fontSize: '0.8rem',
          cursor: 'pointer', whiteSpace: 'nowrap',
        }}
      >
        Change model →
      </button>
    </div>
  );
}

// ── Manual extraction modal (copy prompt → paste into Claude/ChatGPT) ────────
function ExtractionPromptModal({ onClose, onPaste }) {
  const [copied, setCopied] = useState(false);
  const [pastedJson, setPastedJson] = useState('');
  const [pasteError, setPasteError] = useState('');

  const handleCopy = () => {
    navigator.clipboard.writeText(MANUAL_EXTRACTION_PROMPT).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleUse = () => {
    if (!pastedJson.trim()) { setPasteError('Paste the JSON from the AI tool first.'); return; }
    try { JSON.parse(pastedJson); }
    catch (_) { setPasteError('That does not look like valid JSON. Make sure to copy only the JSON block.'); return; }
    onPaste(pastedJson.trim());
    onClose();
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: '16px',
    }}>
      <div style={{
        background: 'white', borderRadius: '14px', padding: '28px',
        maxWidth: '640px', width: '100%', maxHeight: '90vh',
        overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
          <div>
            <h3 style={{ margin: 0, color: '#2d3748' }}>Extract with External AI</h3>
            <p style={{ margin: '4px 0 0', color: '#718096', fontSize: '0.85rem' }}>
              Use Claude.ai or ChatGPT to extract data from your document
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#718096', lineHeight: 1 }}>×</button>
        </div>

        {/* Steps */}
        <ol style={{ paddingLeft: '20px', margin: '0 0 20px', color: '#4a5568', fontSize: '0.9rem', lineHeight: '1.8' }}>
          <li>Copy the extraction prompt below</li>
          <li>Open Claude.ai or ChatGPT in a new tab</li>
          <li>Upload your document and paste the prompt</li>
          <li>Copy the JSON response and paste it here</li>
        </ol>

        {/* External AI links */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
          <a href="https://claude.ai" target="_blank" rel="noopener noreferrer"
            style={{ flex: 1, padding: '10px', background: '#fdf4ff', border: '1px solid #e9d8fd', borderRadius: '8px', textAlign: 'center', color: '#6b21a8', textDecoration: 'none', fontWeight: '600', fontSize: '0.875rem' }}>
            Open Claude.ai ↗
          </a>
          <a href="https://chat.openai.com" target="_blank" rel="noopener noreferrer"
            style={{ flex: 1, padding: '10px', background: '#f0fff4', border: '1px solid #9ae6b4', borderRadius: '8px', textAlign: 'center', color: '#276749', textDecoration: 'none', fontWeight: '600', fontSize: '0.875rem' }}>
            Open ChatGPT ↗
          </a>
        </div>

        {/* Prompt to copy */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <label style={{ fontWeight: '600', color: '#2d3748', fontSize: '0.875rem' }}>Extraction Prompt</label>
            <button onClick={handleCopy}
              style={{ padding: '5px 14px', background: copied ? '#48bb78' : '#2b6cb0', color: 'white', border: 'none', borderRadius: '6px', fontSize: '0.8rem', cursor: 'pointer', fontWeight: '600' }}>
              {copied ? '✓ Copied!' : 'Copy Prompt'}
            </button>
          </div>
          <textarea readOnly value={MANUAL_EXTRACTION_PROMPT} rows={8}
            onClick={e => e.target.select()}
            style={{ width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.78rem', fontFamily: 'monospace', resize: 'vertical', background: '#f7fafc', boxSizing: 'border-box', color: '#4a5568' }}
          />
        </div>

        {/* Paste result back */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', fontWeight: '600', color: '#2d3748', fontSize: '0.875rem', marginBottom: '8px' }}>
            Paste JSON result here
          </label>
          <textarea value={pastedJson} onChange={e => { setPastedJson(e.target.value); setPasteError(''); }}
            placeholder='{"patient_name": "...", "lab_results": [...], ...}'
            rows={5}
            style={{ width: '100%', padding: '10px', border: `1px solid ${pasteError ? '#fc8181' : '#e2e8f0'}`, borderRadius: '8px', fontSize: '0.85rem', fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box' }}
          />
          {pasteError && <p style={{ color: '#c53030', fontSize: '0.8rem', marginTop: '4px' }}>{pasteError}</p>}
        </div>

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button onClick={onClose}
            style={{ padding: '10px 20px', background: '#edf2f7', color: '#4a5568', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
            Cancel
          </button>
          <button onClick={handleUse} disabled={!pastedJson.trim()}
            style={{ padding: '10px 24px', background: pastedJson.trim() ? '#2b6cb0' : '#a0aec0', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: pastedJson.trim() ? 'pointer' : 'not-allowed' }}>
            Use this JSON
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
function DocumentUpload({ user }) {
  const navigate   = useNavigate();
  const { patientId } = useParams();
  const fileInputRef = useRef(null);

  const [selectedFile,   setSelectedFile]   = useState(null);
  const [documentType,   setDocumentType]   = useState('');
  const [documentDate,   setDocumentDate]   = useState('');
  const [notes,          setNotes]          = useState('');
  const [clinicalNotes,  setClinicalNotes]  = useState('');
  const [selectedTags,   setSelectedTags]   = useState([]);
  const [loading,        setLoading]        = useState(false);
  const [error,          setError]          = useState('');
  const [success,        setSuccess]        = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploaded,       setUploaded]       = useState(false);

  // LLM extraction state
  const [llmConfig,    setLlmConfig]    = useState(null);   // null = not configured
  const [extracting,   setExtracting]   = useState(false);
  const [extractDone,  setExtractDone]  = useState(false);
  const [extractModel, setExtractModel] = useState('');
  const [extractError, setExtractError] = useState('');
  const [showManualModal, setShowManualModal] = useState(false);

  const token   = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  // Load LLM config on mount
  useEffect(() => {
    api.get(`${API_URL}/auth/llm-config`, { headers })
      .then(res => {
        const c = res.data.config;
        if (c && c.configured) setLlmConfig(c);
      })
      .catch(() => {});
  }, []); // eslint-disable-line

  // Redirect after successful upload
  useEffect(() => {
    if (!uploaded) return;
    const t = setTimeout(() => navigate(`/patients/${patientId}`), 2000);
    return () => clearTimeout(t);
  }, [uploaded, navigate, patientId]);

  const handleFileSelect = e => {
    const file = e.target.files[0];
    if (file) { setSelectedFile(file); setError(''); setExtractDone(false); setNotes(''); setExtractError(''); }
  };

  const handleDrop = e => {
    e.preventDefault(); e.stopPropagation();
    const file = e.dataTransfer.files[0];
    if (file) { setSelectedFile(file); setError(''); setExtractDone(false); setNotes(''); setExtractError(''); }
  };

  const formatFileSize = bytes => {
    if (!bytes) return '0 bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return (bytes / Math.pow(1024, i)).toFixed(1) + ' ' + ['bytes','KB','MB','GB'][i];
  };

  const isFileValid = file => {
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['pdf','jpg','jpeg','png','docx','doc','gif','bmp','tiff','webp'].includes(ext))
      return `File type not allowed.`;
    if (file.size > 20 * 1024 * 1024) return 'File size exceeds 20MB limit';
    return null;
  };

  // ── Manual extraction: user pasted JSON from external AI ────────────────
  const handleManualPaste = (jsonStr) => {
    try {
      const extracted = JSON.parse(jsonStr);
      setNotes(JSON.stringify(extracted, null, 2));
      setExtractModel('external AI');
      setExtractDone(true);
      if (!documentDate && extracted.report_date) setDocumentDate(extracted.report_date);
    } catch (_) {}
  };

  // ── Step 1: Extract with LLM ─────────────────────────────────────────────
  const handleExtract = useCallback(async () => {
    if (!selectedFile) return;
    const fileErr = isFileValid(selectedFile);
    if (fileErr) { setError(fileErr); return; }

    setExtracting(true); setExtractError(''); setNotes('');

    try {
      // Read file as base64
      const fileB64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload  = e => resolve(e.target.result.split(',')[1]); // strip data:...;base64,
        reader.onerror = () => reject(new Error('Could not read file'));
        reader.readAsDataURL(selectedFile);
      });

      const res = await api.post(
        `${API_URL}/ai/extract-document`,
        { file_data: fileB64, mime_type: selectedFile.type || 'application/octet-stream' },
        { headers, timeout: 150000 }
      );

      const extracted = res.data.extracted || {};
      setNotes(JSON.stringify(extracted, null, 2));
      setExtractModel(res.data.model || '');
      setExtractDone(true);

      // Auto-fill document date from report_date if not already set
      if (!documentDate && extracted.report_date) setDocumentDate(extracted.report_date);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Extraction failed';
      setExtractError(msg);
    } finally {
      setExtracting(false);
    }
  }, [selectedFile, documentDate, headers]);

  // ── Step 2: Upload ───────────────────────────────────────────────────────
  const handleSubmit = async e => {
    e.preventDefault();
    setError(''); setSuccess(''); setLoading(true); setUploadProgress(0);

    if (!selectedFile)    { setError('Please select a file'); setLoading(false); return; }
    if (!documentType)    { setError('Please select a document type'); setLoading(false); return; }
    const fileErr = isFileValid(selectedFile);
    if (fileErr)          { setError(fileErr); setLoading(false); return; }

    if (notes && notes.trim()) {
      try { JSON.parse(notes); }
      catch (_) {
        setError('Extracted Medical Data must be valid JSON. Correct the format or clear it.');
        setLoading(false); return;
      }
    }

    try {
      const finalNotes = notes + (clinicalNotes ? '\n\nCLINICAL_NOTES: ' + clinicalNotes : '');
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('patient_id', patientId);
      formData.append('document_type', documentType);
      if (finalNotes)    formData.append('notes', finalNotes);
      if (documentDate)  formData.append('document_date', documentDate);

      const response = await api.post(`${API_URL}/documents/upload`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
        onUploadProgress: ev => setUploadProgress(Math.round((ev.loaded * 100) / ev.total)),
      });

      const documentId = response.data.document?.id;
      if (documentId && selectedTags.length > 0) {
        for (const tag of selectedTags) {
          await axios.post(`${API_URL}/tags/document/${documentId}`, { tag_id: tag.id }, { headers })
            .catch(() => {});
        }
      }

      setUploaded(true);
      let msg = 'Document uploaded successfully!';
      const pd = response.data.parsed_data;
      if (pd?.found) {
        const parts = [];
        if (pd.lab_results?.length)  parts.push(`${pd.lab_results.length} lab result(s)`);
        if (pd.radiology?.length)    parts.push(`${pd.radiology.length} radiology report(s)`);
        if (pd.prescriptions?.length) parts.push(`${pd.prescriptions.length} prescription(s)`);
        if (parts.length) msg += ` Data imported: ${parts.join(', ')}.`;
      }
      setSuccess(msg + ' Redirecting…');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload document');
      setLoading(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  const hasLLM   = !!llmConfig;
  const canUpload = !hasLLM || extractDone;   // require extraction when LLM is connected

  if (!patientId) return (
    <div className="dashboard">
      <Navbar user={user} onLogout={handleLogout} />
      <div className="dashboard-content"><div className="api-error">No patient ID provided</div></div>
    </div>
  );

  return (
    <div className="dashboard">
      <Navbar user={user} onLogout={handleLogout} />

      {showManualModal && (
        <ExtractionPromptModal
          onClose={() => setShowManualModal(false)}
          onPaste={handleManualPaste}
        />
      )}

      <div className="dashboard-content">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2>Upload Document</h2>
          <Link to={`/patients/${patientId}`} style={{ color: '#2b6cb0', textDecoration: 'none' }}>
            ← Back to Patient
          </Link>
        </div>

        {error   && <div className="api-error"     style={{ marginBottom: '20px' }}>{error}</div>}
        {success && <div style={{ background: '#c6f6d5', color: '#22543d', padding: '12px', borderRadius: '8px', marginBottom: '20px' }}>{success}</div>}

        {!uploaded ? (
          <div style={{ background: 'white', padding: '32px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', maxWidth: '700px', margin: '0 auto' }}>

            {/* LLM banner */}
            <LLMBanner
              llmConfig={llmConfig}
              onChangeClick={() => navigate('/settings?tab=apis')}
            />

            <form onSubmit={handleSubmit}>

              {/* File picker */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: '#4a5568', fontWeight: '500' }}>Select File *</label>
                <div
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
                  onDrop={handleDrop}
                  style={{
                    border: selectedFile ? '2px solid #48bb78' : '2px dashed #cbd5e0',
                    borderRadius: '12px', padding: '40px', textAlign: 'center', cursor: 'pointer',
                    background: selectedFile ? '#f0fff4' : '#f7fafc', transition: 'border-color 0.2s',
                    marginBottom: '12px',
                  }}
                >
                  <input ref={fileInputRef} type="file" onChange={handleFileSelect} style={{ display: 'none' }}
                    accept=".pdf,.jpg,.jpeg,.png,.docx,.doc,.gif,.bmp,.tiff,.webp" />
                  <div style={{ fontSize: '3rem', marginBottom: '10px' }}>📁</div>
                  <p style={{ color: '#4a5568', marginBottom: '6px' }}>
                    {selectedFile ? 'Click to change file' : 'Drag & drop or click to browse'}
                  </p>
                  <p style={{ color: '#718096', fontSize: '0.875rem' }}>PDF, JPG, PNG, DOCX (max 20 MB)</p>
                </div>

                {selectedFile && (
                  <div style={{ background: '#edf2f7', padding: '14px 16px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '1.8rem' }}>
                      {selectedFile.type.includes('pdf') ? '📄' : selectedFile.type.includes('image') ? '🖼️' : '📝'}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: '600', color: '#2d3748', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {selectedFile.name}
                      </div>
                      <div style={{ color: '#718096', fontSize: '0.85rem' }}>{formatFileSize(selectedFile.size)}</div>
                    </div>
                    <button type="button" onClick={() => { setSelectedFile(null); setExtractDone(false); setNotes(''); setExtractError(''); }}
                      style={{ padding: '4px 10px', background: '#fed7d7', color: '#c53030', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' }}>
                      Remove
                    </button>
                  </div>
                )}
              </div>

              {/* Document type */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', color: '#4a5568', fontWeight: '500' }}>Document Type *</label>
                <select value={documentType} onChange={e => setDocumentType(e.target.value)}
                  style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '1rem' }} required>
                  <option value="">Select document type</option>
                  {DOCUMENT_TYPES.map(dt => <option key={dt.value} value={dt.value}>{dt.label}</option>)}
                </select>
              </div>

              {/* Document date */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', color: '#4a5568', fontWeight: '500' }}>Document Date (optional)</label>
                <input type="date" value={documentDate} onChange={e => setDocumentDate(e.target.value)}
                  style={{ width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '1rem' }} />
              </div>

              {/* ── EXTRACTION SECTION ── */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label style={{ color: '#4a5568', fontWeight: '500' }}>
                    📋 Extracted Medical Data (JSON)
                  </label>
                  {selectedFile && !extractDone && (
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      {hasLLM && (
                        <button type="button" onClick={handleExtract} disabled={extracting}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '6px',
                            padding: '7px 16px', background: extracting ? '#718096' : '#2b6cb0',
                            color: 'white', border: 'none', borderRadius: '8px',
                            fontSize: '0.875rem', fontWeight: '600', cursor: extracting ? 'not-allowed' : 'pointer',
                          }}>
                          {extracting ? (
                            <>
                              <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite', fontSize: '1rem' }}>⟳</span>
                              Extracting…
                            </>
                          ) : '✨ Extract with AI'}
                        </button>
                      )}
                      <button type="button" onClick={() => setShowManualModal(true)}
                        style={{
                          padding: '7px 14px', background: 'none',
                          color: '#4a5568', border: '1px solid #cbd5e0', borderRadius: '8px',
                          fontSize: '0.82rem', cursor: 'pointer', whiteSpace: 'nowrap',
                        }}>
                        Use Claude / ChatGPT
                      </button>
                    </div>
                  )}
                  {extractDone && (
                    <span style={{ background: '#c6f6d5', color: '#22543d', padding: '4px 12px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: '600' }}>
                      ✓ Extracted via {extractModel}
                    </span>
                  )}
                </div>

                {/* extraction error */}
                {extractError && (
                  <div style={{ background: '#fff5f5', color: '#c53030', border: '1px solid #fed7d7', borderRadius: '8px', padding: '10px 14px', marginBottom: '10px', fontSize: '0.875rem' }}>
                    <strong>Extraction failed:</strong> {extractError}
                    <br /><span style={{ color: '#718096', fontSize: '0.8rem' }}>You can enter the JSON manually below, or try again.</span>
                  </div>
                )}

                {/* shimmer while extracting */}
                {extracting ? (
                  <div style={{ border: '2px solid #bee3f8', borderRadius: '8px', background: '#f7fafc', minHeight: '140px' }}>
                    <ShimmerLines />
                  </div>
                ) : (
                  <textarea
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    placeholder={hasLLM && selectedFile && !extractDone
                      ? 'Click "Extract Data with AI" above to auto-fill this field…'
                      : '{"lab_results": [...], "vitals": [...], "report_date": "YYYY-MM-DD"}'}
                    rows={notes ? Math.min(Math.max(notes.split('\n').length + 1, 6), 20) : 5}
                    style={{
                      width: '100%', padding: '12px', border: `2px solid ${extractDone ? '#9ae6b4' : '#e2e8f0'}`,
                      borderRadius: '8px', fontSize: '0.875rem', resize: 'vertical',
                      fontFamily: 'monospace', boxSizing: 'border-box',
                      background: extractDone ? '#f0fff4' : 'white',
                    }}
                  />
                )}

                <small style={{ color: '#718096', fontSize: '0.8rem' }}>
                  {extractDone
                    ? 'Review and edit if needed, then click Upload.'
                    : hasLLM
                      ? 'Auto-extract with your connected AI, or use Claude.ai / ChatGPT manually.'
                      : 'Use "Claude / ChatGPT" button above to get the extraction prompt, or paste your own JSON.'}
                </small>
                <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
              </div>

              {/* LLM not configured — show hint */}
              {!hasLLM && (
                <div style={{ background: '#fffbeb', border: '1px solid #fbd38d', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px', fontSize: '0.875rem', color: '#744210' }}>
                  💡 Connect an AI model in <Link to="/settings?tab=apis" style={{ color: '#2b6cb0' }}>Settings → APIs</Link> for one-click extraction — or use the <button type="button" onClick={() => setShowManualModal(true)} style={{ background: 'none', border: 'none', color: '#2b6cb0', cursor: 'pointer', padding: 0, fontSize: '0.875rem', textDecoration: 'underline' }}>Claude / ChatGPT</button> option above.
                </div>
              )}

              {/* Clinical notes */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#2d3748' }}>
                  📝 Clinical Notes
                  <span style={{ fontSize: '0.8rem', color: '#718096', fontWeight: '400', marginLeft: '8px' }}>Your personal notes about this visit</span>
                </label>
                <textarea value={clinicalNotes} onChange={e => setClinicalNotes(e.target.value)}
                  placeholder="e.g. Doctor advised follow-up in 3 months. Prescribed Metformin 500mg."
                  rows={3}
                  style={{ width: '100%', padding: '10px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: '0.9rem', resize: 'vertical', boxSizing: 'border-box' }} />
              </div>

              {/* Tags */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#2d3748' }}>
                  🏷️ Tags
                </label>
                <TagSelector patientId={patientId} selectedTags={selectedTags} onChange={setSelectedTags} headers={headers} />
              </div>

              {/* Upload progress */}
              {loading && (
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ color: '#4a5568', fontSize: '0.875rem' }}>Uploading…</span>
                    <span style={{ color: '#4a5568', fontSize: '0.875rem', fontWeight: '600' }}>{uploadProgress}%</span>
                  </div>
                  <div style={{ width: '100%', background: '#e2e8f0', borderRadius: '9999px', height: '10px', overflow: 'hidden' }}>
                    <div style={{ width: `${uploadProgress}%`, background: '#48bb78', height: '100%', transition: 'width 0.3s ease' }} />
                  </div>
                </div>
              )}

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: '12px', marginTop: '28px', paddingTop: '20px', borderTop: '1px solid #e2e8f0', flexWrap: 'wrap', alignItems: 'center' }}>
                <button type="submit" disabled={loading || extracting}
                  style={{
                    padding: '12px 32px', background: canUpload ? '#2b6cb0' : '#a0aec0',
                    color: 'white', border: 'none', borderRadius: '8px',
                    fontSize: '1rem', fontWeight: '600', cursor: (loading || extracting) ? 'not-allowed' : 'pointer',
                  }}>
                  {loading ? 'Uploading…' : 'Upload Document'}
                </button>

                {/* Skip extraction option when LLM configured but not yet extracted */}
                {hasLLM && !extractDone && !extracting && selectedFile && (
                  <button type="submit" disabled={loading}
                    style={{
                      padding: '12px 20px', background: 'none', color: '#718096',
                      border: '1px solid #e2e8f0', borderRadius: '8px',
                      fontSize: '0.875rem', cursor: loading ? 'not-allowed' : 'pointer',
                    }}>
                    Skip extraction & upload
                  </button>
                )}

                <button type="button" onClick={() => navigate(`/patients/${patientId}`)} disabled={loading}
                  style={{ padding: '12px 24px', background: '#edf2f7', color: '#4a5568', border: 'none', borderRadius: '8px', fontSize: '1rem', cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>

              {hasLLM && !extractDone && selectedFile && !extracting && (
                <p style={{ color: '#718096', fontSize: '0.8rem', marginTop: '8px' }}>
                  Tip: click <strong>Extract Data with AI</strong> first for automatic data import.
                </p>
              )}
            </form>
          </div>
        ) : (
          // Success screen
          <div style={{ background: 'white', padding: '60px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', maxWidth: '560px', margin: '0 auto', textAlign: 'center' }}>
            <div style={{ fontSize: '4rem', marginBottom: '20px' }}>✅</div>
            <h2 style={{ color: '#2d3748', marginBottom: '12px' }}>Document Uploaded!</h2>
            <p style={{ color: '#718096', marginBottom: '28px' }}>Redirecting to patient dashboard…</p>
            <button onClick={() => navigate(`/patients/${patientId}`)} style={{ padding: '12px 32px', background: '#2b6cb0', color: 'white', border: 'none', borderRadius: '8px', fontSize: '1rem', fontWeight: '600', cursor: 'pointer' }}>
              Go Now
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default DocumentUpload;
